import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Form } from 'antd';
import { useQuery } from '@apollo/client';
import type { NamePath } from '@xbcb/ui-types';
import {
  ComplianceCheck,
  ComplianceCheckStatus,
  ComplianceCheckType,
  GetComplianceRecommendationDocument,
} from '__generated__/graphql';
import {
  getLatestComplianceCheck,
  parseComplianceCheckRequestId,
} from 'libs/complianceChecks';
import { DEFAULT_DESTINATION_COUNTRY_CODE } from 'constants/complianceDefaults';
import { clearFields } from 'libs/clearFields';

export type PgaDisclaimRecommendationView =
  | 'none'
  | 'loading'
  | 'error'
  | 'recommended'
  | 'accepted'
  | 'declined';

export interface ComplianceRecommendationDetails {
  loading: boolean;
  disclaimerCode?: string;
  reason?: string;
}

interface UseComplianceRecommendationParams {
  /** Line-item path that holds `complianceChecks`. */
  lineNamePath: NamePath;
  /** Absolute PGA path, e.g. [...line, 'tariffs', idx, 'pga', flag]. */
  fullNamePath: NamePath;
  /** The PGA flag this tab represents (== tariffFlagCode). */
  pgaFlag: string;
  destinationCountryCode?: string;
}

export interface ComplianceRecommendationState {
  status?: ComplianceCheckStatus;
  view: PgaDisclaimRecommendationView;
  details: ComplianceRecommendationDetails;
  accept: () => void;
  decline: () => void;
  /**
   * Flip an AI-applied disclaim (`AI_VALIDATED`) to `USER_MANUAL_OVERRIDE` when
   * the operator manually edits the flag. No-op for any other status, so a
   * manual edit of a non-AI disclaim leaves the check untouched. Mirrors the
   * HTS classification flow (useHtsClassification).
   */
  markManualOverride: () => void;
  /**
   * The actual disclaim decision for this flag, independent of the check
   * status: true when the disclaim toggle is set or a disclaimer code is
   * present. The banner uses this so an `AI_VALIDATED` "no disclaim" verdict is
   * not mislabeled as disclaimed.
   */
  isDisclaimed: boolean;
}

// Drives the PGA Disclaim AI accept/decline flow for a single tariff flag. The
// verdict is a PGA_DISCLAIM compliance check whose `requestId` encodes
// `batchId#identifier#flag`; we match this tab's flag, parse the identifier, and
// query with the flag as `tariffFlagCode`. Accept/decline persist the decision
// back into the line's complianceChecks so it survives a reload.
export const useComplianceRecommendation = ({
  lineNamePath,
  fullNamePath,
  pgaFlag,
  destinationCountryCode = DEFAULT_DESTINATION_COUNTRY_CODE,
}: UseComplianceRecommendationParams): ComplianceRecommendationState => {
  const form = Form.useFormInstance();
  const linePath = lineNamePath as (string | number)[];
  const pgaPath = fullNamePath as (string | number)[];

  // Guards for the manual-edit detector below: skip the first (initialization)
  // pass, and skip the field writes the hook makes itself in accept().
  const initializedRef = useRef(false);
  const programmaticUpdateRef = useRef(false);
  const prevDisclaimKeyRef = useRef<string | undefined>(undefined);

  const complianceChecks = Form.useWatch(
    [...linePath, 'complianceChecks'],
    form,
  ) as ComplianceCheck[] | undefined;

  const check = getLatestComplianceCheck(
    complianceChecks,
    ComplianceCheckType.PGA_DISCLAIM,
    (c) => parseComplianceCheckRequestId(c.requestId).flag === pgaFlag,
  );
  const status = check?.status ?? undefined;
  const { identifier } = parseComplianceCheckRequestId(check?.requestId);

  const isTerminal =
    status === ComplianceCheckStatus.FAILED ||
    status === ComplianceCheckStatus.COMPLETED;
  // ACCEPTED is a human accept (retained for reuse); AI_VALIDATED is the backend
  // auto-disclaim the PGA disclaim response handler now writes. Both are "applied".
  const isAccepted =
    status === ComplianceCheckStatus.ACCEPTED ||
    status === ComplianceCheckStatus.AI_VALIDATED;

  // Only fetch once there's a verdict to act on (terminal or an applied disclaim).
  const shouldFetch = Boolean(identifier) && (isTerminal || isAccepted);
  const { data, loading, error } = useQuery(
    GetComplianceRecommendationDocument,
    {
      variables: {
        identifier: identifier as string,
        tariffFlagCode: pgaFlag,
        destinationCountryCode,
      },
      skip: !shouldFetch,
    },
  );

  const payload = data?.getComplianceRecommendation;
  const result = payload?.result;

  const details = useMemo<ComplianceRecommendationDetails>(
    () => ({
      loading: shouldFetch && loading,
      disclaimerCode: result?.disclaimerCode ?? undefined,
      reason: result?.reason ?? undefined,
    }),
    [shouldFetch, loading, result],
  );

  const hasRecommendation =
    payload?.availability === 'AVAILABLE' && Boolean(result?.isEligible);

  const view: PgaDisclaimRecommendationView = isAccepted
    ? 'accepted'
    : status === ComplianceCheckStatus.DECLINED
    ? 'declined'
    : isTerminal
    ? details.loading
      ? 'loading'
      : error
      ? 'error'
      : hasRecommendation
      ? 'recommended'
      : 'none'
    : 'none';

  const setStatusForFlag = useCallback(
    (nextStatus: ComplianceCheckStatus) => {
      const checks =
        (form.getFieldValue([...linePath, 'complianceChecks']) as
          | ComplianceCheck[]
          | undefined) ?? [];
      const idx = checks.findIndex(
        (c) =>
          c.type === ComplianceCheckType.PGA_DISCLAIM &&
          parseComplianceCheckRequestId(c.requestId).flag === pgaFlag,
      );
      if (idx === -1) return;
      form.setFields([
        {
          name: [...linePath, 'complianceChecks'],
          value: checks.map((c, i) =>
            i === idx ? { ...c, status: nextStatus } : c,
          ),
        },
      ]);
    },
    [form, linePath, pgaFlag],
  );

  const accept = useCallback(() => {
    // accept() writes disclaim/disclaimerCode/programCode itself; flag the next
    // watch pass as programmatic so the manual-edit detector ignores it.
    programmaticUpdateRef.current = true;
    form.setFields([{ name: [...pgaPath, 'disclaim'], value: true }]);
    // NHTSA (DT1) requires program code OFF when disclaimed.
    if (pgaFlag === 'DT1') {
      form.setFields([{ name: [...pgaPath, 'programCode'], value: 'OFF' }]);
    }
    clearFields({
      form,
      fullNamePath: pgaPath,
      exclude: ['agencyCode', 'processingCode', 'programCode', 'disclaim'],
    })();
    if (details.disclaimerCode) {
      form.setFields([
        { name: [...pgaPath, 'disclaimerCode'], value: details.disclaimerCode },
      ]);
    }
    setStatusForFlag(ComplianceCheckStatus.ACCEPTED);
  }, [form, pgaPath, pgaFlag, details.disclaimerCode, setStatusForFlag]);

  const decline = useCallback(() => {
    setStatusForFlag(ComplianceCheckStatus.DECLINED);
  }, [setStatusForFlag]);

  // An operator manual edit of an AI-applied disclaim replaces the AI verdict:
  // flip AI_VALIDATED -> USER_MANUAL_OVERRIDE so the "disclaimed by AI" banner
  // drops and the operator-override state persists (read back from the
  // compliance check on reload). Only AI_VALIDATED is overridden; every other
  // status (incl. an already-overridden one) is left untouched.
  const markManualOverride = useCallback(() => {
    if (status !== ComplianceCheckStatus.AI_VALIDATED) return;
    setStatusForFlag(ComplianceCheckStatus.USER_MANUAL_OVERRIDE);
  }, [status, setStatusForFlag]);

  // Detect an operator manual edit of this flag. We watch the operator-editable
  // sub-fields (disclaim toggle, disclaimer code, program code) and, on a real
  // change, invalidate an AI-applied disclaim. Mirrors useHtsClassification:
  // skip the first (initialization) pass and the hook's own accept() writes
  // (flagged via programmaticUpdateRef) so only genuine edits count.
  const disclaimField = Form.useWatch([...pgaPath, 'disclaim'], form);
  const disclaimerCodeField = Form.useWatch(
    [...pgaPath, 'disclaimerCode'],
    form,
  );
  const programCodeField = Form.useWatch([...pgaPath, 'programCode'], form);

  useEffect(() => {
    // Don't arm the manual-edit detector until the line record has hydrated.
    // The entry loads asynchronously (Apollo), so the disclaim fields populate
    // undefined -> value a render after mount; without this guard that
    // hydration is mistaken for an operator edit and wrongly flips an
    // AI_VALIDATED disclaim to USER_MANUAL_OVERRIDE (the banner then renders
    // nothing). Mirrors useHtsClassification's unhydrated bail. complianceChecks
    // and the pga fields hydrate in the same pass, so once checks are present
    // the init pass seeds the baseline from the loaded field values.
    if (complianceChecks === undefined) return;

    const key = JSON.stringify([
      disclaimField,
      disclaimerCodeField,
      programCodeField,
    ]);

    if (!initializedRef.current) {
      initializedRef.current = true;
      prevDisclaimKeyRef.current = key;
      return;
    }
    if (programmaticUpdateRef.current) {
      programmaticUpdateRef.current = false;
      prevDisclaimKeyRef.current = key;
      return;
    }
    if (key !== prevDisclaimKeyRef.current) {
      prevDisclaimKeyRef.current = key;
      markManualOverride();
    }
  }, [
    complianceChecks,
    disclaimField,
    disclaimerCodeField,
    programCodeField,
    markManualOverride,
  ]);

  // The actual disclaim decision for this flag, independent of the check
  // status: a disclaim is applied when the toggle is set or a disclaimer code
  // is present (lines.ts derives the "Disclaim?" checkbox from disclaimerCode on
  // load). The banner keys off this so an AI_VALIDATED "no disclaim" verdict is
  // not mislabeled as "disclaimed by AI".
  const isDisclaimed = disclaimField === true || Boolean(disclaimerCodeField);

  // Banner rendering keys off status plus the disclaim decision: AI_VALIDATED
  // shows "disclaimed by AI" only when the flag is actually disclaimed, and an
  // AI-validated "no disclaim" otherwise. COMPLETED (evaluated, not eligible)
  // and USER_MANUAL_OVERRIDE (operator replaced the AI disclaim) are unmapped
  // and render nothing. IN_PROGRESS and FAILED banners pass through.
  return {
    status,
    view,
    details,
    accept,
    decline,
    markManualOverride,
    isDisclaimed,
  };
};
